import { useCallback, useEffect, useMemo, useState } from "react"
import { Alert, Button, Card, Input, Modal, Select, Space, Tag } from "antd"
import { Prompt } from "react-router-dom"
import { v4 as uuidv4 } from "uuid"
import type {
  EquipmentContext,
  EquipmentContextAssignment,
  EquipmentContextQuestionnaire,
  EquipmentConfigurationWrite,
  CatalogTemplate,
  ResolvedCatalog,
} from "../../types/regulatory"
import { allCatalogPages, regulatoryApi } from "../../utils/regulatoryApi"
import { configurationDraftKey } from "../../utils/equipmentContexts"
import { useCatalogResource } from "../../pages/Regulatory/useCatalog"
function AssignmentRow({
  assignment,
  onChange,
  onRemove,
  templates,
  resolved,
  wholeModules,
  wholeUuid,
  disabled,
  component,
}: {
  assignment: EquipmentContextAssignment
  onChange: (a: EquipmentContextAssignment) => void
  onRemove: () => void
  templates: CatalogTemplate[]
  resolved?: ResolvedCatalog
  wholeModules: Map<string, string>
  wholeUuid?: string
  disabled: boolean
  component: boolean
}) {
  const revisions = useCatalogResource(
    useCallback(
      (signal) =>
        assignment.templateUuid
          ? allCatalogPages(
              (f, s) => regulatoryApi.revisions(assignment.templateUuid, f, s),
              signal,
            )
          : Promise.resolve([]),
      [assignment.templateUuid],
    ),
  )
  const available = templates.filter((t) => t.scope !== "site" && !t.archived)
  const templateOptions = available.map((t) => ({
    value: t.uuid,
    label: `${t.name} · ${t.validated ? "Revu" : "Non revu"}`,
  }))
  if (assignment.templateUuid && !templateOptions.some((t) => t.value === assignment.templateUuid))
    templateOptions.push({
      value: assignment.templateUuid,
      label: resolved?.templates[0]?.name ?? `Modèle historique · ${assignment.templateUuid}`,
    })
  const common = resolved?.templates.filter((t) => t.scope === "common") ?? []
  const sharingOptions = common
    .filter((m) => wholeModules.has(m.revisionUuid))
    .map((m) => ({ value: m.revisionUuid, label: `${m.name} · v${m.sourceVersion}` }))
  for (const b of assignment.bindings)
    if (!sharingOptions.some((o) => o.value === b.moduleRevisionUuid))
      sharingOptions.push({
        value: b.moduleRevisionUuid,
        label: `Liaison à vérifier · ${b.moduleRevisionUuid}`,
      })
  return (
    <Card size="small" className="reg-context-assignment">
      <Space direction="vertical" style={{ width: "100%" }}>
        <Select
          aria-label="Modèle du composant"
          placeholder="Choisir un modèle"
          showSearch
          optionFilterProp="label"
          value={assignment.templateUuid || undefined}
          options={templateOptions}
          style={{ width: "100%" }}
          disabled={disabled}
          onChange={(templateUuid) =>
            onChange({ ...assignment, templateUuid, templateRevisionUuid: null, bindings: [] })
          }
        />
        <Select
          aria-label="Révision du composant"
          placeholder="Choisir une révision exacte"
          value={assignment.templateRevisionUuid ?? undefined}
          loading={revisions.loading}
          disabled={disabled || revisions.loading || !!revisions.error || !assignment.templateUuid}
          style={{ width: "100%" }}
          options={(revisions.data ?? []).map((r) => ({
            value: r.uuid,
            label: `v${r.sourceVersion} · ${r.definition.validated ? "Revu" : "Non revu"} · ${r.uuid}`,
          }))}
          onChange={(templateRevisionUuid) =>
            onChange({ ...assignment, templateRevisionUuid, bindings: [] })
          }
        />
        {revisions.error && (
          <Alert
            type="error"
            message={revisions.error.message}
            action={<Button onClick={revisions.reload}>Réessayer</Button>}
          />
        )}
        {resolved && (
          <Space wrap>
            <Tag>{resolved.controls.length} contrôles avant partage</Tag>
            <Tag color={resolved.allReviewed ? "green" : "orange"}>
              {resolved.allReviewed ? "Modules revus" : "Revue à compléter"}
            </Tag>
            <Tag>
              {resolved.coverageGaps} compléments absents · {resolved.interpretationReserves}{" "}
              réserves
            </Tag>
          </Space>
        )}
        {component && common.length > 0 && (
          <>
            <label>Réutiliser les constats de l’ensemble pour ces modules communs</label>
            <Select
              aria-label="Modules communs partagés"
              mode="multiple"
              placeholder="Aucun partage : constats propres au composant"
              value={assignment.bindings.map((b) => b.moduleRevisionUuid)}
              options={sharingOptions}
              style={{ width: "100%" }}
              disabled={disabled || !wholeUuid}
              onChange={(values: string[]) =>
                onChange({
                  ...assignment,
                  bindings: values.map((moduleRevisionUuid) => ({
                    moduleRevisionUuid,
                    destinationContextUuid: wholeUuid!,
                  })),
                })
              }
            />
            <small>
              Le partage exige la même révision exacte dans l’ensemble de l’équipement. Il n’est
              jamais automatique.
            </small>
            {!sharingOptions.length && (
              <Alert
                type="info"
                message="Aucune révision commune identique n’est affectée à l’ensemble."
              />
            )}
          </>
        )}
        <Button danger disabled={disabled} onClick={onRemove}>
          Retirer ce modèle
        </Button>
      </Space>
    </Card>
  )
}
export function EquipmentContextEditor({
  data,
  blocked,
  onPublished,
  onDirtyChange,
  guardNavigation = true,
}: {
  data: EquipmentContextQuestionnaire
  blocked: boolean
  onDirtyChange?: (dirty: boolean) => void
  guardNavigation?: boolean
  onPublished: () => void
}) {
  const [editing, setEditing] = useState(false)
  const persisted = JSON.stringify(data.configuration)
  const [contexts, setContexts] = useState<EquipmentContext[]>(() =>
    structuredClone(data.configuration.contexts),
  )
  const [baseline, setBaseline] = useState(persisted)
  const [version, setVersion] = useState(data.configuration.version)
  const [latest, setLatest] = useState<EquipmentContextQuestionnaire>()
  const [resetReview, setResetReview] = useState<{
    data: EquipmentContextQuestionnaire
    draftKey: string
  }>()
  const [preview, setPreview] = useState<EquipmentContextQuestionnaire>()
  const [previewKey, setPreviewKey] = useState("")
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<string>(),
    [conflict, setConflict] = useState(false)
  const draft: EquipmentConfigurationWrite = { expectedVersion: version, contexts }
  const draftKey = configurationDraftKey(draft)
  const dirty = JSON.stringify(contexts) !== JSON.stringify(JSON.parse(baseline).contexts)
  useEffect(() => {
    if (data.configuration.version > version && !dirty) {
      setContexts(structuredClone(data.configuration.contexts))
      setVersion(data.configuration.version)
      setBaseline(persisted)
      setPreview(undefined)
      setPreviewKey("")
    }
  }, [persisted, baseline, dirty])
  useEffect(() => {
    onDirtyChange?.(dirty)
    return () => onDirtyChange?.(false)
  }, [dirty, onDirtyChange])
  useEffect(() => {
    if (!dirty) return
    const guard = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", guard)
    return () => window.removeEventListener("beforeunload", guard)
  }, [dirty])
  const templates = useCatalogResource(
    useCallback(
      (signal) =>
        editing ? allCatalogPages(regulatoryApi.templates, signal) : Promise.resolve([]),
      [editing],
    ),
  )
  const pins = JSON.stringify(
    (editing ? contexts : [])
      .flatMap((c) => c.assignments)
      .filter((a) => a.templateUuid && a.templateRevisionUuid)
      .map((a) => ({
        uuid: a.uuid,
        templateUuid: a.templateUuid,
        revision: a.templateRevisionUuid!,
      })),
  )
  const resolved = useCatalogResource(
    useCallback(
      async (signal) =>
        Object.fromEntries(
          await Promise.all(
            (JSON.parse(pins) as { uuid: string; templateUuid: string; revision: string }[]).map(
              async (a) =>
                [a.uuid, await regulatoryApi.resolved(a.templateUuid, a.revision, signal)] as const,
            ),
          ),
        ),
      [pins],
    ),
  )
  const whole = contexts.find((c) => c.kind === "whole")
  const wholeModules = useMemo(
    () =>
      new Map(
        whole?.assignments.flatMap(
          (a) =>
            resolved.data?.[a.uuid]?.templates
              .filter((t) => t.scope === "common")
              .map((t) => [t.revisionUuid, `${t.name} · v${t.sourceVersion}`] as const) ?? [],
        ) ?? [],
      ),
    [whole, resolved.data],
  )
  const disabled = blocked || busy
  const complete =
    contexts.length > 0 &&
    contexts.filter((c) => c.kind === "whole").length === 1 &&
    contexts.every(
      (c) =>
        c.label.trim() &&
        (c.kind === "whole" || c.assignments.length > 0) &&
        c.assignments.every((a) => a.templateUuid && a.templateRevisionUuid),
    )
  const change = (id: string, patch: Partial<EquipmentContext>) =>
    setContexts((old) => old.map((c) => (c.uuid === id ? { ...c, ...patch } : c)))
  const reset = () => {
    setContexts(structuredClone((latest ?? data).configuration.contexts))
    setVersion((latest ?? data).configuration.version)
    setBaseline(JSON.stringify((latest ?? data).configuration))
    setPreview(undefined)
    setPreviewKey("")
    setError(undefined)
    setConflict(false)
  }
  const run = async (publish: boolean) => {
    setBusy(true)
    setError(undefined)
    try {
      const result = publish
        ? await regulatoryApi.publishEquipmentConfiguration(data.configuration.equipmentUuid, draft)
        : await regulatoryApi.previewEquipmentConfiguration(data.configuration.equipmentUuid, draft)
      if (publish) {
        setContexts(result.configuration.contexts)
        setVersion(result.configuration.version)
        setBaseline(JSON.stringify(result.configuration))
        setPreview(undefined)
        setPreviewKey("")
        setEditing(false)
        onPublished()
      } else {
        setConflict(false)
        setPreview(result)
        setPreviewKey(draftKey)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setConflict((e as { status?: number }).status === 409)
    } finally {
      setBusy(false)
    }
  }
  const prepareAnswerReset = async () => {
    if (disabled) return
    setBusy(true)
    try {
      const candidate = await regulatoryApi.previewEquipmentConfiguration(
        data.configuration.equipmentUuid,
        { ...draft, resetAnswers: { confirm: false, confirmationToken: "" } },
      )
      if (!candidate.resetImpact)
        throw new Error(
          "L’API ne prend pas encore en charge la suppression des réponses. Redémarrez l’API mise à jour.",
        )
      setResetReview({ data: candidate, draftKey })
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }
  const confirmAnswerReset = async () => {
    if (disabled || !resetReview?.data.resetImpact || resetReview.draftKey !== draftKey) return
    setBusy(true)
    try {
      const result = await regulatoryApi.publishEquipmentConfiguration(
        data.configuration.equipmentUuid,
        {
          ...draft,
          resetAnswers: {
            confirm: true,
            confirmationToken: resetReview.data.resetImpact.confirmationToken,
          },
        },
      )
      setContexts(result.configuration.contexts)
      setVersion(result.configuration.version)
      setBaseline(JSON.stringify(result.configuration))
      setPreview(undefined)
      setPreviewKey("")
      setResetReview(undefined)
      setError(undefined)
      setConflict(false)
      setEditing(false)
      onPublished()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setResetReview(undefined)
      setConflict(true)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Card
      id="equipment-configuration"
      className="reg-context-config"
      title="Composants et modèles"
      extra={
        <Button onClick={() => setEditing((value) => !value)}>
          {editing ? "Fermer la configuration" : "Configurer les composants"}
        </Button>
      }
    >
      <Modal
        title="Supprimer les réponses et remplacer la grille ?"
        open={!!resetReview}
        onCancel={() => {
          if (!busy) setResetReview(undefined)
        }}
        onOk={confirmAnswerReset}
        okText="Supprimer les réponses et appliquer"
        cancelText="Annuler"
        confirmLoading={busy}
        closable={!busy}
        maskClosable={!busy}
        cancelButtonProps={{ disabled: busy }}
        okButtonProps={{
          danger: true,
          disabled: blocked || !resetReview?.data.resetImpact || resetReview.draftKey !== draftKey,
        }}
      >
        <Alert
          type="warning"
          showIcon
          message="Suppression définitive des réponses de cet équipement"
          description="Cela efface toutes les réponses, y compris celles des composants et les constats communs. Les réponses de la mission et des autres équipements sont conservées."
        />
        <p>
          <strong>{resetReview?.data.resetImpact?.answerCount ?? 0} réponse(s)</strong> et{" "}
          {resetReview?.data.resetImpact?.photoAssociations ?? 0} association(s) photo seront
          supprimées. Les fichiers photo restent conservés.
        </p>
        <ul>
          {resetReview?.data.resetImpact?.contexts.map((context) => (
            <li key={context.contextUuid ?? "legacy"}>
              {context.label} : {context.answerCount} réponse(s)
            </li>
          ))}
        </ul>
        <p>
          La nouvelle configuration contient {resetReview?.data.ownedControlCount ?? 0} contrôles
          uniques. La suppression et le changement de grille sont enregistrés ensemble ; une erreur
          conserve les données précédentes.
        </p>
      </Modal>
      <Prompt
        when={guardNavigation && dirty && !busy}
        message="La configuration des composants n’est pas publiée. Quitter et abandonner le brouillon ?"
      />
      {!editing && (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Space wrap>
            {data.configuration.contexts.map((c) => (
              <Tag key={c.uuid}>
                {c.label} · {c.assignments.length} modèle(s)
              </Tag>
            ))}
          </Space>
          <small>
            {data.configuration.enabled
              ? "Configuration par composants active"
              : "Affectation simple"}{" "}
            · version {data.configuration.version}
          </small>
          {dirty && (
            <Alert
              type="warning"
              message="Un brouillon de configuration reste à publier ou abandonner."
            />
          )}
        </Space>
      )}
      {editing && (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Alert
            type="info"
            message={
              data.configuration.enabled
                ? "Configuration par composants active"
                : "Préparer une configuration par composants"
            }
            description="Les constats déjà enregistrés restent dans leur contexte d’origine. La publication active les composants ; les anciennes applications ne pourront plus modifier ce questionnaire. Les composants sont propres à cette inspection."
          />
          {blocked && (
            <Alert
              type="warning"
              message="Enregistrez la fiche et attendez la confirmation des réponses avant de modifier les composants."
            />
          )}
          {!data.configuration.contexts.length && (
            <Button
              disabled={disabled}
              onClick={() =>
                setContexts([
                  {
                    uuid: uuidv4(),
                    kind: "whole",
                    label: "Ensemble de l’équipement",
                    assignments: [],
                  },
                ])
              }
            >
              Configurer l’ensemble de l’équipement
            </Button>
          )}
          {contexts.map((c, index) => (
            <Card
              data-context-editor={c.uuid}
              size="small"
              key={c.uuid}
              title={c.kind === "whole" ? "Ensemble de l’équipement" : `Composant ${index}`}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Input
                  aria-label="Nom du contexte"
                  value={c.label}
                  maxLength={150}
                  disabled={disabled}
                  onChange={(e) => change(c.uuid, { label: e.target.value })}
                />
                {c.assignments.map((a) => (
                  <AssignmentRow
                    key={a.uuid}
                    assignment={a}
                    templates={templates.data ?? []}
                    resolved={resolved.data?.[a.uuid]}
                    wholeModules={wholeModules}
                    wholeUuid={whole?.uuid}
                    component={c.kind === "component"}
                    disabled={disabled || templates.loading || !!templates.error}
                    onChange={(next) =>
                      change(c.uuid, {
                        assignments: c.assignments.map((x) => (x.uuid === a.uuid ? next : x)),
                      })
                    }
                    onRemove={() =>
                      change(c.uuid, {
                        assignments: c.assignments.filter((x) => x.uuid !== a.uuid),
                      })
                    }
                  />
                ))}
                <Space wrap>
                  <Button
                    disabled={disabled || c.assignments.length >= 20}
                    onClick={() =>
                      change(c.uuid, {
                        assignments: [
                          ...c.assignments,
                          {
                            uuid: uuidv4(),
                            templateUuid: "",
                            templateRevisionUuid: null,
                            bindings: [],
                          },
                        ],
                      })
                    }
                  >
                    Ajouter un modèle
                  </Button>
                  {c.kind === "component" && (
                    <>
                      <Button
                        disabled={disabled || index <= 1}
                        onClick={() =>
                          setContexts((old) => {
                            const next = [...old]
                            ;[next[index - 1], next[index]] = [next[index]!, next[index - 1]!]
                            return next
                          })
                        }
                      >
                        Monter
                      </Button>
                      <Button
                        danger
                        disabled={disabled}
                        onClick={() => setContexts((old) => old.filter((x) => x.uuid !== c.uuid))}
                      >
                        Retirer ce composant
                      </Button>
                    </>
                  )}
                </Space>
              </Space>
            </Card>
          ))}
          <Button
            disabled={disabled || !whole || contexts.length >= 100}
            onClick={() =>
              setContexts((old) => [
                ...old,
                {
                  uuid: uuidv4(),
                  kind: "component",
                  label: `Composant ${old.filter((c) => c.kind === "component").length + 1}`,
                  assignments: [],
                },
              ])
            }
          >
            Ajouter un composant
          </Button>
          {templates.error && (
            <Alert
              type="error"
              message={templates.error.message}
              action={<Button onClick={templates.reload}>Réessayer</Button>}
            />
          )}
          {resolved.error && (
            <Alert
              type="error"
              message={resolved.error.message}
              action={<Button onClick={resolved.reload}>Réessayer</Button>}
            />
          )}
          {error && (
            <Alert
              type="error"
              message={error}
              description={
                conflict
                  ? "Le brouillon est conservé. Une publication concurrente ou des réponses existantes peuvent empêcher ce changement."
                  : undefined
              }
            />
          )}
          {conflict && (
            <Button
              danger
              disabled={disabled || !complete || !!resolved.error || resolved.loading}
              onClick={prepareAnswerReset}
            >
              Remplacer la grille en effaçant toutes les réponses…
            </Button>
          )}
          {latest && (
            <Alert
              type="info"
              message={`Version enregistrée : ${latest.configuration.version}`}
              description={
                <>
                  <p>
                    {latest.configuration.contexts.map((c) => c.label).join(" ; ")}. Votre brouillon
                    est conservé.
                  </p>
                  <Button onClick={reset}>
                    Repartir de cette version et abandonner le brouillon
                  </Button>
                </>
              }
            />
          )}
          {preview && previewKey === draftKey && (
            <Alert
              type="success"
              message={`Aperçu validé : ${preview.ownedControlCount} contrôles uniques`}
              description={`${preview.configuration.contexts.length} contextes ; ${preview.controls.filter((c) => c.displayContextUuid !== c.answerContextUuid).length} références à des constats partagés. Aucun changement enregistré par cet aperçu.`}
            />
          )}
          <Space wrap>
            <Button
              loading={busy}
              disabled={disabled || !complete || !!resolved.error || resolved.loading}
              onClick={() => run(false)}
            >
              Vérifier la configuration
            </Button>
            <Button
              type="primary"
              disabled={disabled || conflict || previewKey !== draftKey || !preview}
              onClick={() => run(true)}
            >
              Publier les composants
            </Button>
            <Button disabled={busy} onClick={reset}>
              Abandonner le brouillon
            </Button>
            {conflict && (
              <Button
                disabled={busy}
                onClick={async () => {
                  try {
                    setLatest(
                      await regulatoryApi.equipmentQuestionnaire(data.configuration.equipmentUuid),
                    )
                  } catch (e) {
                    setError(String(e))
                  }
                }}
              >
                Voir la configuration enregistrée
              </Button>
            )}
          </Space>
        </Space>
      )}
    </Card>
  )
}
